import { setTopbar, lucideIcons, esc } from '../app.js';
import { toast, confirmDialog, promptDialog } from '../dialog.js';
import { isTauri, backend } from '../backend.js';
import { getToken } from '../auth-store.js';

export async function renderUsbVault(container) {
  setTopbar('USB Vault', '');

  if (!isTauri) {
    container.innerHTML = `
      <div class="card" style="max-width:600px">
        <div class="msg msg-error">
          USB Vault requires the BlindKeep desktop app.
          <br><br>
          Download it at <strong>blindkeep.com/desktop</strong> to use a USB drive as offline encrypted storage.
        </div>
      </div>`;
    return;
  }

  container.innerHTML = '<p class="loading">Loading...</p>';

  try {
    const [status, drives] = await Promise.all([
      backend.syncStatus(),
      backend.detectDrives(),
    ]);

    let html = '';

    // Status card
    html += `
      <div class="card" style="max-width:600px;margin-bottom:24px">
        <div class="detail-label">Status</div>
        <div class="detail-value" style="display:flex;align-items:center;gap:8px">
          <span class="status-dot ${status.unlocked && status.usb_connected ? 'online' : 'offline'}"></span>
          ${status.unlocked && status.usb_connected
            ? `Connected &mdash; ${status.item_count} items on USB`
            : status.usb_connected
              ? 'USB connected, vault locked'
              : 'No USB vault connected'}
        </div>
        ${status.has_pin ? '<div class="detail-label" style="margin-top:8px">Device PIN</div><div class="detail-value">Enabled</div>' : ''}
      </div>`;

    // Detected drives
    html += '<div class="section-title">Detected Drives</div>';

    if (drives.length === 0) {
      html += '<p style="color:var(--text-secondary);margin-bottom:24px">No removable drives detected. Plug in a USB drive to get started.</p>';
    } else {
      drives.forEach((drive, i) => {
        html += `
          <div class="card drive-card" style="max-width:600px;margin-bottom:12px;display:flex;align-items:center;gap:16px">
            <div style="flex-shrink:0"><i data-lucide="hard-drive" style="width:24px;height:24px;color:var(--accent)"></i></div>
            <div style="flex:1">
              <div style="font-weight:600">${esc(drive.label || 'USB Drive')}</div>
              <div style="font-size:12px;color:var(--text-secondary)">${esc(drive.path)}</div>
            </div>
            <div>
              ${drive.has_vault
                ? `<button class="btn btn-primary btn-sm connect-drive-btn" data-path="${esc(drive.path)}">Connect</button>`
                : `<button class="btn btn-secondary btn-sm init-drive-btn" data-path="${esc(drive.path)}">Initialize</button>`}
            </div>
          </div>`;
      });
    }

    // Choose folder button (always available)
    html += `
      <div style="margin-top:16px;max-width:600px">
        <button class="btn btn-secondary btn-sm" id="choose-folder-btn">
          <i data-lucide="folder-open" style="width:14px;height:14px"></i> Choose Folder...
        </button>
        <span style="font-size:12px;color:var(--text-secondary);margin-left:8px">Pick any folder to use as vault storage</span>
      </div>`;

    // Sync section (only when connected)
    if (status.unlocked && status.usb_connected) {
      html += `
        <div class="section-title" style="margin-top:32px">Sync</div>
        <div class="card" style="max-width:600px">
          <div id="sync-status" style="margin-bottom:16px;color:var(--text-secondary)">Ready to sync</div>
          <div style="display:flex;gap:12px">
            <button class="btn btn-primary btn-sm" id="sync-now-btn">
              <i data-lucide="refresh-cw" style="width:14px;height:14px"></i> Sync Now
            </button>
          </div>
          <div id="sync-report" style="margin-top:16px"></div>
        </div>`;

      // PIN management
      html += `
        <div class="section-title" style="margin-top:32px">Device PIN</div>
        <div class="card" style="max-width:600px">
          <p style="color:var(--text-secondary);margin-bottom:16px;font-size:14px">
            A device PIN adds an extra layer of protection if your USB drive is lost or stolen.
            An attacker would need both your password and the PIN to access the vault.
          </p>
          ${status.has_pin
            ? `<div style="display:flex;gap:12px">
                <button class="btn btn-secondary btn-sm" id="change-pin-btn">Change PIN</button>
                <button class="btn btn-danger btn-sm" id="remove-pin-btn">Remove PIN</button>
              </div>`
            : `<button class="btn btn-secondary btn-sm" id="set-pin-btn">Set Device PIN</button>`}
        </div>`;
    }

    // Refresh button
    html += `
      <div style="margin-top:32px">
        <button class="btn btn-secondary btn-sm" id="refresh-drives-btn">
          <i data-lucide="refresh-cw" style="width:14px;height:14px"></i> Refresh
        </button>
      </div>`;

    container.innerHTML = html;
    lucideIcons();

    // --- Event handlers ---

    // Choose folder
    const chooseFolderBtn = document.getElementById('choose-folder-btn');
    if (chooseFolderBtn) {
      chooseFolderBtn.onclick = async () => {
        try {
          const path = await window.__TAURI_INTERNALS__.invoke('pick_folder');
          if (!path) return; // cancelled

          // Check if it already has a vault
          try {
            await backend.setUsbPath(path);
            toast('Vault connected from ' + path, 'success');
            renderUsbVault(container);
            return;
          } catch (_) {}

          // No existing vault — offer to initialize
          const pin = await promptDialog('Optional: set a device PIN (leave empty for none)', { placeholder: '4-8 digit PIN' });
          if (pin === null) return;

          await backend.initUsb(path, pin || null);
          toast('Vault initialized at ' + path, 'success');
          renderUsbVault(container);
        } catch (err) {
          toast(err.message || String(err));
        }
      };
    }

    // Initialize drive
    container.querySelectorAll('.init-drive-btn').forEach(btn => {
      btn.onclick = async () => {
        const path = btn.dataset.path;
        const pin = await promptDialog('Optional: set a device PIN (leave empty for none)', { placeholder: '4-8 digit PIN' });
        if (pin === null) return; // cancelled

        btn.disabled = true;
        btn.textContent = 'Initializing...';
        try {
          await backend.initUsb(path, pin || null);
          toast('USB vault initialized', 'success');
          renderUsbVault(container); // refresh
        } catch (err) {
          toast(err.message || err);
          btn.disabled = false;
          btn.textContent = 'Initialize';
        }
      };
    });

    // Connect existing vault
    container.querySelectorAll('.connect-drive-btn').forEach(btn => {
      btn.onclick = async () => {
        const path = btn.dataset.path;
        btn.disabled = true;
        btn.textContent = 'Connecting...';
        try {
          await backend.setUsbPath(path);
          toast('USB vault connected', 'success');
          renderUsbVault(container);
        } catch (err) {
          toast(err.message || err);
          btn.disabled = false;
          btn.textContent = 'Connect';
        }
      };
    });

    // Sync now
    const syncBtn = document.getElementById('sync-now-btn');
    if (syncBtn) {
      syncBtn.onclick = async () => {
        const statusEl = document.getElementById('sync-status');
        const reportEl = document.getElementById('sync-report');
        syncBtn.disabled = true;
        statusEl.textContent = 'Syncing...';

        try {
          const token = getToken();
          const { BASE } = await import('../api.js');
          const report = await window.__TAURI_INTERNALS__.invoke('sync_now', {
            apiBaseUrl: BASE || window.location.origin,
            token: token,
          });

          statusEl.textContent = 'Sync complete';
          reportEl.innerHTML = `
            <div style="font-size:13px;color:var(--text-secondary)">
              <div>Pulled: <strong>${report.pulled}</strong> items from server</div>
              <div>Pushed: <strong>${report.pushed}</strong> items to server</div>
              ${report.deleted_local ? `<div>Deleted locally: <strong>${report.deleted_local}</strong></div>` : ''}
              ${report.deleted_remote ? `<div>Deleted on server: <strong>${report.deleted_remote}</strong></div>` : ''}
              ${report.errors.length ? `<div class="msg msg-error" style="margin-top:8px">${report.errors.map(esc).join('<br>')}</div>` : ''}
            </div>`;
          toast('Sync complete', 'success');
        } catch (err) {
          statusEl.textContent = 'Sync failed';
          reportEl.innerHTML = `<div class="msg msg-error">${esc(String(err))}</div>`;
          toast('Sync failed: ' + err);
        }
        syncBtn.disabled = false;
      };
    }

    // PIN management
    const setPinBtn = document.getElementById('set-pin-btn');
    if (setPinBtn) {
      setPinBtn.onclick = async () => {
        const pin = await promptDialog('Enter a device PIN (4-8 digits)', { placeholder: 'PIN' });
        if (!pin) return;
        try {
          await backend.setPin(pin);
          toast('Device PIN set', 'success');
          renderUsbVault(container);
        } catch (err) { toast(err.message || err); }
      };
    }

    const changePinBtn = document.getElementById('change-pin-btn');
    if (changePinBtn) {
      changePinBtn.onclick = async () => {
        const pin = await promptDialog('Enter new device PIN', { placeholder: 'New PIN' });
        if (!pin) return;
        try {
          await backend.setPin(pin);
          toast('Device PIN changed', 'success');
        } catch (err) { toast(err.message || err); }
      };
    }

    const removePinBtn = document.getElementById('remove-pin-btn');
    if (removePinBtn) {
      removePinBtn.onclick = async () => {
        const ok = await confirmDialog('Remove the device PIN? The vault will only be protected by your password.', { danger: true });
        if (!ok) return;
        try {
          await backend.removePin();
          toast('Device PIN removed', 'success');
          renderUsbVault(container);
        } catch (err) { toast(err.message || err); }
      };
    }

    // Refresh
    const refreshBtn = document.getElementById('refresh-drives-btn');
    if (refreshBtn) {
      refreshBtn.onclick = () => renderUsbVault(container);
    }

  } catch (err) {
    container.innerHTML = `<div class="msg msg-error">${esc(String(err))}</div>`;
  }
}
